import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { StatusDot } from "@/components/brand/status-dot";
import { useNav } from "../nav.tsx";
import { ErrorNote, errText, formatWhen, SectionTitle } from "../ui.tsx";
import type { StageProps } from "./EmployeeShell.tsx";

export function DistillStage({ api, detail, reload }: StageProps) {
  const { go } = useNav();
  const e = detail.employee;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string>();
  const job = detail.jobs.find((j) => j.kind === "distill");
  const running = job && (job.status === "pending" || job.status === "running");
  const ended = detail.sessions.filter((s) => s.status === "ended").length;
  const ready = detail.sources.filter((s) => s.status === "ready").length;
  const pending = detail.state.sourcesPending;
  const hasDraft = Boolean(detail.state.draftRevisionId);

  return (
    <div className="grid gap-4">
      <SectionTitle
        title="Distill"
        description="A text model reads every interview, lookup and source and writes the employee's context: focus, what it must never do, decision authority, tools and a knowledge digest. Every statement cites where it came from."
      />
      <ErrorNote error={error} />
      {detail.state.draftStale && (
        <Alert variant="warning">
          <AlertTitle>The current draft is stale</AlertTitle>
          <AlertDescription>New knowledge or interviews arrived after it was distilled. Distill again before publishing.</AlertDescription>
        </Alert>
      )}
      <Card>
        <CardHeader>
          <CardTitle>Inputs</CardTitle>
          <CardDescription>
            {ended} finished interview{ended === 1 ? "" : "s"} · {ready} ready source{ready === 1 ? "" : "s"}
            {pending ? ` · ${pending} still being read` : ""}
          </CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap items-center gap-3">
          <Button
            disabled={busy || Boolean(running) || detail.stages.distill === "locked"}
            onClick={async () => {
              setBusy(true);
              setError(undefined);
              try {
                await api.distill(e.id);
                await reload();
              } catch (err) {
                setError(errText(err));
              } finally {
                setBusy(false);
              }
            }}
          >
            {hasDraft ? "Distill again" : "Distill"}
          </Button>
          {running && <StatusDot tone="info" label="Distilling… this can take a minute" pulse />}
          {job?.status === "failed" && (
            <>
              <StatusDot tone="danger" label={`Failed: ${job.error ?? "unknown error"}`} />
              <Button size="sm" variant="outline" onClick={() => void api.retryJob(e.id, job.id).then(reload)}>
                Retry
              </Button>
            </>
          )}
          {job?.status === "done" && !detail.state.draftStale && <StatusDot tone="success" label={`Distilled ${formatWhen(job.updatedAt)}`} />}
          {detail.stages.distill === "locked" && <span className="text-sm text-muted-foreground">Finish at least one interview, and wait for every source to be read.</span>}
        </CardContent>
      </Card>
      {hasDraft && (
        <div>
          <Button variant="outline" onClick={() => go(`/employees/${e.id}/review`)}>
            Review the draft
          </Button>
        </div>
      )}
    </div>
  );
}
